import React, { useRef } from 'react';
import { Upload } from 'lucide-react';
interface TongueData {
  images: {
    frontal: string | null;
    sublingual: string | null;
  };
  colors: string[];
  shapes: string[];
  coating: string[];
  moisture: string[];
  sublingualVeins: string[];
}
interface Props {
  data: TongueData;
  onChange: (data: TongueData) => void;
}
const tongueColors = [{
  name: 'Pale',
  indications: ['Yang deficiency', 'Blood deficiency']
}, {
  name: 'Red',
  indications: ['Heat (Full or Empty)']
}, {
  name: 'Deep-Red',
  indications: ['Severe Heat']
}, {
  name: 'Reddish-Purple',
  indications: ['Blood stasis from Heat']
}, {
  name: 'Bluish-Purple',
  indications: ['Blood stasis from Cold']
}, {
  name: 'Blue',
  indications: ['Interior Cold', 'Blood stasis']
}];
const tongueShapes = [{
  name: 'Thin',
  indications: ['Blood deficiency', 'Yin deficiency']
}, {
  name: 'Swollen',
  indications: ['Dampness', 'Phlegm']
}, {
  name: 'Stiff',
  indications: ['Interior Wind', 'Blood stasis']
}, {
  name: 'Flaccid',
  indications: ['Deficiency of Body Fluids']
}, {
  name: 'Cracked',
  indications: ['Full-Heat', 'Yin deficiency']
}, {
  name: 'Tooth-marked',
  indications: ['Spleen Qi deficiency']
}, {
  name: 'Long',
  indications: ['Heat']
}, {
  name: 'Short',
  indications: ['Qi deficiency']
}];
const tongueCoatings = [{
  name: 'White',
  indications: ['Cold']
}, {
  name: 'Yellow',
  indications: ['Heat']
}, {
  name: 'Grey',
  indications: ['Internal Heat or Cold']
}, {
  name: 'Black',
  indications: ['Extreme Heat or Cold']
}, {
  name: 'Thick',
  indications: ['Excess condition']
}, {
  name: 'Thin',
  indications: ['Normal or early stage']
}, {
  name: 'Absent',
  indications: ['Yin deficiency']
}, {
  name: 'Greasy',
  indications: ['Dampness', 'Phlegm']
}];
const tongueMoisture = [{
  name: 'Too wet',
  indications: ['Yang deficiency', 'Dampness']
}, {
  name: 'Dry',
  indications: ['Yin deficiency', 'Heat']
}, {
  name: 'Normal',
  indications: ['Balanced']
}, {
  name: 'Sticky/Slippery',
  indications: ['Dampness', 'Phlegm']
}];
const sublingualVeins = [{
  name: 'Distended/purple veins',
  indications: ['Blood stasis', 'Circulation problem']
}, {
  name: 'Blue/purple',
  indications: ['Qi stagnation', 'Cold', 'Impaired flow']
}, {
  name: 'Thin, pale veins',
  indications: ['Qi & Blood deficiency', 'Weak circulation']
}, {
  name: 'Tortuous/nodular veins',
  indications: ['Chronic stasis', 'Chronic stagnation/disease']
}, {
  name: 'No obvious veins',
  indications: ['Normal', 'Healthy circulation']
}];
export function TongueDiagnosis({
  data,
  onChange
}: Props) {
  const handleImageUpload = (type: 'frontal' | 'sublingual', file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      onChange({
        ...data,
        images: {
          ...data.images,
          [type]: e.target?.result as string
        }
      });
    };
    reader.readAsDataURL(file);
  };
  const toggleSelection = (category: keyof TongueData, value: string) => {
    const current = data[category] as string[];
    const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    onChange({
      ...data,
      [category]: updated
    });
  };
  return <div className="space-y-6" data-id="element-49">
      <div data-id="element-50">
        <h2 className="text-2xl font-bold text-gray-800 mb-4" data-id="element-51">
          👅 Tongue Diagnosis
        </h2>
      </div>
      {/* Image Uploads */}
      <div className="grid md:grid-cols-2 gap-6" data-id="element-52">
        <ImageUploadBox title="Frontal Tongue Image" image={data.images.frontal} onUpload={file => handleImageUpload('frontal', file)} data-id="element-53" />
        <ImageUploadBox title="Sublingual Image (Back of Tongue)" image={data.images.sublingual} onUpload={file => handleImageUpload('sublingual', file)} data-id="element-54" />
      </div>
      {/* Characteristics */}
      <div className="grid md:grid-cols-2 gap-6" data-id="element-55">
        <CheckboxGroup title="Tongue Body Color" items={tongueColors} selected={data.colors} onToggle={value => toggleSelection('colors', value)} data-id="element-56" />
        <CheckboxGroup title="Tongue Body Shape" items={tongueShapes} selected={data.shapes} onToggle={value => toggleSelection('shapes', value)} data-id="element-57" />
      </div>
      <div className="grid md:grid-cols-2 gap-6" data-id="element-58">
        <CheckboxGroup title="Tongue Coating" items={tongueCoatings} selected={data.coating} onToggle={value => toggleSelection('coating', value)} data-id="element-59" />
        <CheckboxGroup title="Tongue Moisture" items={tongueMoisture} selected={data.moisture} onToggle={value => toggleSelection('moisture', value)} data-id="element-60" />
      </div>
      <div className="grid md:grid-cols-2 gap-6" data-id="element-61">
        <CheckboxGroup title="Sublingual Veins" items={sublingualVeins} selected={data.sublingualVeins} onToggle={value => toggleSelection('sublingualVeins', value)} data-id="element-62" />
      </div>
      {/* Tongue-Organ Mapping */}
      <div className="bg-gradient-to-b from-pink-100 to-red-100 rounded-lg p-6" data-id="element-63">
        <h3 className="text-lg font-semibold mb-4" data-id="element-64">Tongue-Organ Mapping</h3>
        <div className="space-y-2" data-id="element-65">
          <div className="bg-pink-200 p-4 rounded-t-full text-center font-medium" data-id="element-66">
            Tip: Heart ❤️
          </div>
          <div className="grid grid-cols-3 gap-2" data-id="element-67">
            <div className="bg-yellow-200 p-4 rounded text-center text-sm" data-id="element-68">
              Liver/GB
            </div>
            <div className="bg-orange-200 p-4 rounded text-center font-medium" data-id="element-69">
              Center: Stomach/Spleen
            </div>
            <div className="bg-yellow-200 p-4 rounded text-center text-sm" data-id="element-70">
              Liver/GB
            </div>
          </div>
          <div className="bg-blue-200 p-4 rounded-b-full text-center font-medium" data-id="element-71">
            Root: Kidney 🫘
          </div>
        </div>
      </div>
    </div>;
}
function ImageUploadBox({
  title,
  image,
  onUpload
}: {
  title: string;
  image: string | null;
  onUpload: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return <div data-id="element-72">
      <h3 className="text-lg font-semibold mb-2" data-id="element-73">{title}</h3>
      <div onClick={() => inputRef.current?.click()} className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-green-500 transition-colors" data-id="element-74">
        {image ? <img src={image} alt={title} className="w-full h-48 object-cover rounded" data-id="element-75" /> : <>
            <Upload className="w-12 h-12 mx-auto mb-2 text-gray-400" data-id="element-76" />
            <p className="text-gray-600" data-id="element-77">
              Click to upload {title.toLowerCase()}
            </p>
          </>}
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && onUpload(e.target.files[0])} data-id="element-78" />
      </div>
    </div>;
}
function CheckboxGroup({
  title,
  items,
  selected,
  onToggle
}: {
  title: string;
  items: any[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return <div className="bg-gray-50 rounded-lg p-4" data-id="element-79">
      <h3 className="text-lg font-semibold mb-3" data-id="element-80">{title}</h3>
      <div className="space-y-2" data-id="element-81">
        {items.map(item => <label key={item.name} className="flex items-start gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded" data-id="element-82">
            <input type="checkbox" checked={selected.includes(item.name)} onChange={() => onToggle(item.name)} className="mt-1" data-id="element-83" />
            <div className="flex-1" data-id="element-84">
              <span className="font-medium" data-id="element-85">{item.name}</span>
              {item.indications && <p className="text-xs text-gray-600 mt-1" data-id="element-86">
                  {item.indications.join(', ')}
                </p>}
            </div>
          </label>)}
      </div>
    </div>;
}